import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// #894 slice 4 (prep) — data layer for the authz-driven admin route guard.
//
// Calls the current-caller allowed_admin_routes() RPC (migration 20270224),
// which returns the admin route keys the signed-in user may see, derived from
// the authz permission matrix via authorize(). This is the single source the
// cutover will share between ProtectedAdminRoute and AdminSidebar.
//
// LIVE since the #894 cutover: ProtectedAdminRoute and AdminSidebar both read
// it, so a route and its nav item are decided by the same server answer.
//
// FAIL-CLOSED WITHOUT SELF-LOCKOUT. A wired guard must DENY while the query is
// pending or errored — but the hook must NOT bake a lookup FAILURE into a cached
// "ready, empty" success. Doing so would make a single transient RPC outage
// permanently lock every staff member out (React Query can neither retry nor
// surface the error, and the app disables refetch-on-focus/mount globally).
// So the queryFn THROWS on error: the query enters an error state that retries
// and is observable, and `canSeeRoute` denies because there is no data. Only a
// genuine empty result from a healthy RPC is treated as "ready, sees nothing".
//
// Keyed by the authenticated user id and DISABLED until auth resolves, so one
// user can never be served another's cached route set across an in-process
// account switch (the app uses one persistent QueryClient with a 10m cache).
// refetchOnWindowFocus is re-enabled HERE (overriding the app default) so a
// same-user role change is picked up on refocus; the cutover should also
// invalidate this key on auth changes, and SERVER enforcement (RLS / SECURITY
// DEFINER RPCs) stays authoritative regardless.
//
// Route keys are canonical (match ADMIN_ROUTE_KEYS / the route registry),
// e.g. 'index', 'helpers-kyc', 'helpers/:helperId'.

export interface AllowedAdminRoutes {
  routes: Set<string>;
  /** Present only on a successful RPC return; guards deny when data is absent. */
  ready: boolean;
}

async function fetchAllowedAdminRouteKeys(): Promise<string[]> {
  const { data, error } = await supabase.rpc('allowed_admin_routes');
  if (error) {
    // Throw — do NOT cache failure as an empty success. Query goes to an
    // error state (retryable + observable); canSeeRoute denies meanwhile.
    throw new Error(`allowed_admin_routes() failed: ${error.message}`);
  }
  return (data ?? []) as string[];
}

export function useAllowedAdminRoutes() {
  const { user } = useAuth();
  return useQuery({
    // Per-user cache key — never share one user's routes with another.
    queryKey: ['allowed-admin-routes', user?.id ?? null],
    enabled: !!user?.id, // no auth resolved yet ⇒ query stays pending ⇒ deny
    queryFn: async (): Promise<AllowedAdminRoutes> => ({
      routes: new Set<string>(await fetchAllowedAdminRouteKeys()),
      ready: true,
    }),
    retry: 2, // ride out a transient outage rather than locking everyone out
    refetchOnWindowFocus: true, // override the app default so revocations refresh
    staleTime: 60_000,
  });
}

/** The subset of the useAllowedAdminRoutes() query result a guard needs. */
export interface RouteAccessState {
  data?: AllowedAdminRoutes;
  isPending?: boolean;
  isError?: boolean;
}

/**
 * Canonical membership check — ERROR-STATUS AWARE.
 *
 * Denies while pending OR errored, not merely when `data` is absent: after a
 * successful load a later background refetch that fails keeps the STALE `data`,
 * so checking data alone could allow a route the RPC can no longer confirm
 * (e.g. access just revoked). Callers pass the whole query result; we deny
 * unless the latest state is a healthy success. Server RLS/RPC stays the
 * authoritative gate regardless.
 */
export function canSeeRoute(q: RouteAccessState | undefined, routeKey: string): boolean {
  if (!q || q.isPending || q.isError) return false; // deny on unknown / stale-error
  if (!q.data?.ready) return false;                  // deny until a healthy result exists
  return q.data.routes.has(routeKey);
}

/**
 * Console admission in the five-role model: a user may enter the admin console
 * when the server grants them at least one admin route. This is how
 * `marketing` gets in without joining STAFF_ROLES (which would also hand it the
 * broad is_admin_staff() backend surface). Same deny-unless-healthy rule as
 * canSeeRoute.
 */
export function admitsAdminConsole(q: RouteAccessState | undefined): boolean {
  if (!q || q.isPending || q.isError) return false;
  return !!q.data?.ready && q.data.routes.size > 0;
}

/**
 * One-shot form for the sign-in screen, which decides before any page mounts.
 * Fails closed: a lookup error admits no one through this path.
 */
export async function hasAnyAdminRoute(): Promise<boolean> {
  try {
    return (await fetchAllowedAdminRouteKeys()).length > 0;
  } catch {
    return false;
  }
}
